import Modal from "@Components/Modal";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { closeLeadFormModal } from "./data/leadFormReducer";
import styles from "./LeadFormModalWrapper.module.scss";
import { Suspense, lazy, useEffect, useRef } from "react";
import FullPageLoader from "@Components/FullPageLoader";
import { AppState } from "@Store/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useIsDesktop from "hooks/useIsDesktop";
import fetchInventoriesUserHistoryData from "@App/data/fetchInventoriesUserHistoryData";
import ErrorNotification from "@Components/ErrorNotification";
import fetchUserHistory from "@App/data/fetchUserHistory";
import useIsHappyFlow from "hooks/useIsHappyFlow";
import getCookie from "helpers/getCookie";
import { updateInventoriesDataLoaded } from "@App/userHistoryReducer";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
// import setCookie from "helpers/setCookie";
// import useIsHappyFlowBookForm from "hooks/useIsHappyFlowBookForm";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import { startIdleTimer, cancelIdleTimer } from "@Utils/webviewEvents";
import useIsApp from "hooks/useIsApp";
import { IdleTimerStage } from "@Utils/webApputils/analyticEvents";
import lockBodySticky from "@Utils/lockBodySticky";
import unlockBodySticky from "@Utils/unlockBodySticky";
import appStyles from "@App/app.module.scss";

const LeadFormModal = lazy(() => import("./components/LeadFormModal"));

const LeadFormModalV2 = lazy(() => import("../LeadFormV2/LeadFormModal"));

const LeadFormModalWithWrapper = ({ isPropertyPage = false }: { isPropertyPage?: boolean }) => {
  const dispatch = useDispatch();
  const isApp = useIsApp();
  const prevOpenStateRef = useRef(false);

  const handleHideModal = () => {
    dispatch(closeLeadFormModal());
  };

  const { openLeadFormModal, leadFormPage, openingSource, propertyId, showError, isUserLoggedIn } =
    useSelector(
      ({ leadFormModal, propertyPageData, appData }: AppState) => ({
        openLeadFormModal: leadFormModal.openLeadFormModal,
        leadFormPage: leadFormModal.leadFormPage,
        openingSource: leadFormModal.openingSource,
        showError: leadFormModal.showError,
        propertyId: propertyPageData?.id,
        isUserLoggedIn: appData.isUserLoggedIn,
      }),
      shallowEqual,
    );
  const { isWhitelabel } = useWhitelabel();
  const isDesktop = useIsDesktop();
  const { isAbove } = useResponsiveness();
  const isAboveMD = isAbove(SCREEN_SIZE.MD, false);
  const showForDesktop = isDesktop && isAboveMD;
  const isTabletView = isDesktop && !isAboveMD;
  const isHappyFlow = useIsHappyFlow();
  // const isHappyFlowBookForm = useIsHappyFlowBookForm();
  const isLeadFormModalInventoryCardV2 = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.LEAD_FORM_MODAL_INVENTORY_CARD_V2,
  );

  // Start idle timer when user closes/abandons the lead form modal without submitting
  useEffect(() => {
    const wasOpen = prevOpenStateRef.current;
    const isNowClosed = !openLeadFormModal;
    prevOpenStateRef.current = openLeadFormModal;

    if (isApp && wasOpen && isNowClosed && leadFormPage !== 2 && isPropertyPage) {
      const baseUrl = window.location.href.split("?")[0].split("#")[0];
      const redirectUrl = `${baseUrl}?open_lead_form=true`;
      startIdleTimer(IdleTimerStage.LEAD_FORM, redirectUrl);
    }
  }, [isApp, openLeadFormModal, leadFormPage, isPropertyPage]);

  // Prevent background scroll on desktop when modal is open (without overflow:hidden which breaks sticky)
  useEffect(() => {
    if (!showForDesktop || !openLeadFormModal) return undefined;

    lockBodySticky();

    return () => {
      unlockBodySticky();
    };
  }, [showForDesktop, openLeadFormModal]);

  // iOS Safari scroll position fix: when the lead form modal opens on mobile,
  // Safari can shift the underlying page scroll. Save position on mount
  // and restore on unmount so the page doesn't appear cut off when going back.
  useEffect(() => {
    if (showForDesktop || isTabletView || !openLeadFormModal) return undefined;
    const savedScrollY = window.scrollY;

    document.body.classList.add(appStyles.bodyLockedFixed);
    document.body.style.top = `-${savedScrollY}px`;

    return () => {
      document.body.classList.remove(appStyles.bodyLockedFixed);
      document.body.style.top = "";
      window.scrollTo(0, savedScrollY);
    };
  }, [openLeadFormModal, showForDesktop, isTabletView]);

  // Cancel idle timer when lead form is successfully submitted
  useEffect(() => {
    if (isApp && leadFormPage === 2) {
      cancelIdleTimer(IdleTimerStage.LEAD_FORM);
    }
  }, [isApp, leadFormPage]);

  useEffect(() => {
    const isCookiePresent = getCookie("user_flow_token");
    // if (isHappyFlow && isHappyFlowBookForm)
    // setCookie({ cname: "happy-flow-v2", cvalue: isHappyFlow, exdays: 90 });
    // if (!isHappyFlowBookForm) setCookie({ cname: "happy-flow-v2", cvalue: false, exdays: 90 });

    if (isHappyFlow && (isCookiePresent || isUserLoggedIn)) {
      if (isPropertyPage && propertyId) {
        (async () => {
          await fetchInventoriesUserHistoryData(dispatch, propertyId);
        })();
      } else {
        (async () => {
          await fetchUserHistory(dispatch);
        })();
      }
    } else if (isHappyFlow) {
      dispatch(updateInventoriesDataLoaded(true));
    }
  }, [isHappyFlow, isUserLoggedIn]);

  const isReferralEnquiry = openingSource === "referred_by_enquiry";
  const ENABLE_NEW_LF_V2_LAYOUT =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.LEAD_FORM_WITH_STEPPER) &&
    (["book_now", "apply_now"].includes(openingSource) ||
      (showForDesktop &&
        ["clicked_book_enquire_now_property", "search_inventory_enquire"].includes(openingSource)));

  const getModalProps = () => {
    if (showForDesktop) {
      return {
        size: "medium",
        align: "center",
        type: "LeadFormModal",
        noLockBody: true,
        keepBodyLocked: true,
        className: formatClassNames(
          styles.leadFormModalContainer,
          styles.leadFormModalContainerDesktop,
          isHappyFlow && showForDesktop && leadFormPage === 1 ? styles.leadFormHappyFlowStyles : "",
          // eslint-disable-next-line no-nested-ternary
          isHappyFlow && showForDesktop
            ? isLeadFormModalInventoryCardV2
              ? styles.leadFormHappyFlowSuccessStylesNew
              : styles.leadFormHappyFlowSuccessStyles
            : "",
          isHappyFlow && showForDesktop && openingSource === "get_help"
            ? styles.getHelpHappyFlowLeadForm
            : "",
          !isHappyFlow && openingSource === "get_help"
            ? styles.leadFormModalContainerDesktopWithGetHelp
            : "",
          isHappyFlow && showForDesktop && leadFormPage === 1 && ENABLE_NEW_LF_V2_LAYOUT
            ? `${styles.leadFormHappyFlowStylesWithStepper}`
            : "",
        ),
        closeBtnClassName: isHappyFlow
          ? `${styles.desktopCloseButton} ${ENABLE_NEW_LF_V2_LAYOUT ? styles.withStepper : ""}`
          : styles.closeBtnClassName,
        closeIconProps: isHappyFlow ? { stroke: "#111928", size: 16 } : {},
      };
    }

    if (isTabletView) {
      return {
        size: "medium",
        align: "center",
        type: "LeadFormModal",
        className: formatClassNames(
          styles.leadFormModalContainer,
          styles.leadFormModalContainerTablet,
        ),
        closeBtnClassName: isHappyFlow ? styles.desktopCloseButton : styles.closeBtnClassName,
        closeIconProps: isHappyFlow ? { stroke: "#111928", size: 16 } : {},
      };
    }

    return {
      size: "fullScreen",
      type: "LeadFormModal",
      animation: "rightToLeftAnimation",
      className: formatClassNames(styles.leadFormModalContainer),
      closeBtnClassName: isHappyFlow ? styles.mobileCloseButton : "",
      closeIconProps: isHappyFlow ? { stroke: "#111928", size: 14 } : {},
    };
  };

  return (
    <>
      {openLeadFormModal && (
        // @ts-ignore
        <Modal
          hideModal={handleHideModal}
          {...getModalProps()}
          zIndex={1001}
          isHistoryPush={!isDesktop}
        >
          <Suspense
            fallback={
              <FullPageLoader
                // eslint-disable-next-line no-nested-ternary
                width={showForDesktop ? "800px" : isTabletView ? "600px" : "100vw"}
                // eslint-disable-next-line no-nested-ternary
                height={showForDesktop ? "400px" : isTabletView ? "300px" : "100vh"}
              />
            }
          >
            {isHappyFlow ||
            isWhitelabel ||
            openingSource === "virtual_tour_enquiry" ||
            isReferralEnquiry ? (
              <LeadFormModalV2 isTabletView={isTabletView} />
            ) : (
              <LeadFormModal desktop={showForDesktop} />
            )}
          </Suspense>
        </Modal>
      )}
      {showError && <ErrorNotification />}
    </>
  );
};

export default wrapperHOC(LeadFormModalWithWrapper, {
  componentName: "LeadFormModalWithWrapper-Global",
  showForChina: true,
});
